import { useEffect } from "react";

interface ProductSchemaProps {
  name: string;
  description: string;
  image: string;
  sku?: string;
  brand?: string;
  category?: string;
  url?: string;
  priceFrom?: number;
  priceCurrency?: string;
}

/**
 * Injects schema.org Product JSON-LD into <head> for SEO rich results
 * on robot detail pages. Removes itself on unmount so each route only
 * advertises its own product.
 */
const ProductSchema = ({
  name,
  description,
  image,
  sku,
  brand = "PUDU Robotics",
  category = "Commercial Service Robot",
  url,
  priceFrom,
  priceCurrency = "USD",
}: ProductSchemaProps) => {
  useEffect(() => {
    const scriptId = `product-schema-${sku ?? name}`.replace(/\s+/g, "-").toLowerCase();
    const absoluteImage = image.startsWith("http")
      ? image
      : `https://www.neilai.ai${image}`;
    const pageUrl =
      url ??
      (typeof window !== "undefined" ? window.location.href : "https://www.neilai.ai/");

    const data: Record<string, unknown> = {
      "@context": "https://schema.org",
      "@type": "Product",
      name,
      description,
      image: absoluteImage,
      category,
      brand: { "@type": "Brand", name: brand },
      url: pageUrl,
      sku: sku ?? name,
    };

    if (priceFrom) {
      data.offers = {
        "@type": "AggregateOffer",
        priceCurrency,
        lowPrice: priceFrom,
        availability: "https://schema.org/InStock",
        seller: {
          "@type": "Organization",
          name: "Neil AI Global Solutions LLC",
        },
      };
    }

    let script = document.getElementById(scriptId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement("script");
      script.type = "application/ld+json";
      script.id = scriptId;
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(data);

    return () => {
      script?.remove();
    };
  }, [name, description, image, sku, brand, category, url, priceFrom, priceCurrency]);

  return null;
};

export default ProductSchema;
